import { AlertTriangle, CheckCircle2, Radio } from 'lucide-react';
import { formatServerDateShort } from '@/lib/dates';
import { cn } from '@/lib/utils';
import { useBillingHealth } from './hooks';
import type { BillingStatusHealth } from './api';

/**
 * Billing health: recorded usage that has not become revenue.
 *
 * Three of the four statuses are recoverable, which is why the rows exist —
 * `unbilled` usage is posted by provisioning's backfill, and `rejected` /
 * `stranded` can be invoiced by hand. `expired` cannot: it is what an
 * `unbilled` row BECOMES once Stripe's 35-day backdating window closes.
 *
 * So the countdown on `unbilled` is the number that matters here. A total tells
 * you something is stuck; the oldest date tells you how long you have.
 *
 * This panel is also the alerting mechanism. The backend logs these at ERROR,
 * and nothing in this deployment watches ERROR logs — so somebody has to look.
 */

const ROWS: Array<{
  key: 'unbilled' | 'expired' | 'rejected' | 'stranded';
  label: string;
  note: string;
}> = [
  {
    key: 'unbilled',
    label: 'Unbilled',
    note: 'Ran before billing was set up. Recoverable — provisioning posts these.',
  },
  {
    key: 'expired',
    label: 'Expired',
    note: 'Past Stripe’s 35-day window. Needs a manual invoice or a write-off.',
  },
  {
    key: 'rejected',
    label: 'Rejected',
    note: 'Stripe refused the usage. Fix the cause, or invoice by hand.',
  },
  {
    key: 'stranded',
    label: 'Stranded',
    note: 'Too old to retry safely — retrying would bill the customer twice.',
  },
];

/** Under 14 days left is the point at which someone should act now. */
const URGENT_DAYS_LEFT = 14;

function Urgency({ status }: { status: BillingStatusHealth }) {
  if (status.count === 0 || status.days_left === null) return null;
  const urgent = status.days_left <= URGENT_DAYS_LEFT;
  return (
    <span
      className={cn(
        'text-[12px] font-medium',
        urgent ? 'text-destructive' : 'text-muted',
      )}
    >
      {status.days_left === 0
        ? 'window closed'
        : `${status.days_left} day${status.days_left === 1 ? '' : 's'} left`}
    </span>
  );
}

export function BillingHealthPanel() {
  const health = useBillingHealth();

  if (health.isLoading) {
    return <p className="text-muted text-[13px]">Loading billing health…</p>;
  }
  if (health.isError || !health.data) {
    return (
      <p className="text-destructive text-[13px]">
        Couldn&rsquo;t load billing health.
      </p>
    );
  }

  const h = health.data;
  const clean =
    h.total_at_risk === 0 &&
    h.stuck_events.count === 0 &&
    h.failing_events.count === 0;

  return (
    <section
      className="border border-line rounded-xl p-5"
      aria-label="Billing health"
    >
      <div className="flex items-center justify-between gap-4">
        <h3 className="flex items-center gap-2 text-[13px] font-semibold text-ink">
          <Radio className="size-4" />
          Billing health
        </h3>
        {clean ? (
          <span className="flex items-center gap-1.5 text-[12px] text-muted">
            <CheckCircle2 className="size-4" />
            Nothing stuck
          </span>
        ) : (
          <span className="text-[12px] font-medium text-destructive">
            {h.total_tokens_at_risk.toLocaleString('en-US')} tokens unbilled
          </span>
        )}
      </div>

      {!clean && (
        <div className="mt-3 divide-y divide-line">
          {ROWS.map(({ key, label, note }) => {
            const status = h[key];
            if (status.count === 0) return null;
            return (
              <div key={key} className="py-2.5">
                <div className="flex items-baseline justify-between gap-4">
                  <span className="text-[13px] font-medium text-ink">
                    {label}
                    <span className="text-muted font-normal">
                      {' · '}
                      {status.count} run{status.count === 1 ? '' : 's'}
                      {' · '}
                      {status.tokens.toLocaleString('en-US')} tokens
                    </span>
                  </span>
                  <Urgency status={status} />
                </div>
                <p className="text-muted text-[12px] mt-0.5">{note}</p>
                {status.oldest && (
                  <p className="text-muted text-[12px]">
                    Oldest: {formatServerDateShort(status.oldest)}
                  </p>
                )}
              </div>
            );
          })}

          {h.failing_events.count > 0 && (
            <div className="py-2.5">
              <div className="flex items-baseline justify-between gap-4">
                <span className="text-[13px] font-medium text-ink">
                  Webhook events failing
                  <span className="text-muted font-normal">
                    {' · '}
                    {h.failing_events.count}
                  </span>
                </span>
                <span className="text-[12px] font-medium text-state-warning-text">
                  {h.failing_events.max_attempts} attempts
                </span>
              </div>
              <p className="text-muted text-[12px] mt-0.5">
                Stripe is still retrying these, so fixing the handler is enough —
                the next delivery carries the fix. Once Stripe stops after about
                three days they become stuck, and only a replay recovers them.
                {h.failing_events.last_error
                  ? ` Failing with ${h.failing_events.last_error}.`
                  : ''}
              </p>
              {h.failing_events.oldest && (
                <p className="text-muted text-[12px]">
                  Oldest: {formatServerDateShort(h.failing_events.oldest)}
                </p>
              )}
            </div>
          )}

          {h.stuck_events.count > 0 && (
            <div className="py-2.5">
              <div className="flex items-baseline justify-between gap-4">
                <span className="text-[13px] font-medium text-ink">
                  Stuck webhook events
                  <span className="text-muted font-normal">
                    {' · '}
                    {h.stuck_events.count}
                  </span>
                </span>
                <span
                  className={cn(
                    'text-[12px] font-medium',
                    h.stuck_events.replayable ? 'text-muted' : 'text-destructive',
                  )}
                >
                  {h.stuck_events.replayable ? 'replayable' : 'past retention'}
                </span>
              </div>
              <p className="text-muted text-[12px] mt-0.5">
                No redelivery is coming — either a worker died mid-handler, or
                the retries ran out. An unprocessed payment means a customer who
                paid and stays blocked.
                {h.stuck_events.replayable
                  ? ' Replay them with GET /v1/events.'
                  : ' Past Stripe’s 30-day retention, so replay is no longer possible.'}
              </p>
              {h.stuck_events.oldest && (
                <p className="text-muted text-[12px]">
                  Oldest: {formatServerDateShort(h.stuck_events.oldest)}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {!clean && (
        <p className="flex items-start gap-2 mt-3 text-[12px] text-muted">
          <AlertTriangle className="size-4 shrink-0 mt-0.5" />
          Nothing watches these automatically — this panel is the alert.
        </p>
      )}
    </section>
  );
}
